'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowDown, ArrowUp, ArrowUpDown, Hourglass, SearchX } from 'lucide-react';
import type { ScreenerColumn, ScreenerStockRow } from '@/types/screener';
import InfoTooltip from './InfoTooltip';
import MetricValue, { ExchangeBadge } from './MetricValue';
import { stockHref } from './format';

interface ScreenerTableProps {
  columns: ScreenerColumn[];
  rows: ScreenerStockRow[];
  sort: string;
  order: 'asc' | 'desc';
  loading: boolean;
  onSort: (key: string) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
}

const SKELETON_ROWS = 10;

function SortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover/th:opacity-60" />;
  return order === 'desc' ? <ArrowDown className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> : <ArrowUp className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />;
}

// Sticky cells need an opaque background, so they repeat the row's hover colour.
const STICKY_CELL =
  'sticky left-0 z-10 bg-white group-hover:bg-zinc-50 dark:bg-zinc-900 dark:group-hover:bg-zinc-800 border-r border-zinc-100 dark:border-zinc-800';

export default function ScreenerTable({ columns, rows, sort, order, loading, onSort, hasFilters, onClearFilters }: ScreenerTableProps) {
  const router = useRouter();
  const showSkeleton = loading && rows.length === 0;

  if (!loading && rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
        {hasFilters ? (
          <SearchX className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
        ) : (
          <Hourglass className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
        )}
        <p className="mt-4 text-base font-semibold text-zinc-800 dark:text-zinc-200">
          {hasFilters ? 'No stocks match these filters' : 'Stock data is on its way'}
        </p>
        <p className="mt-1 max-w-sm text-sm text-zinc-500 dark:text-zinc-400">
          {hasFilters
            ? 'Try removing a filter or searching for a different name.'
            : 'The screener fills up after the next data refresh. Please check back in a little while.'}
        </p>
        {hasFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="mt-4 text-sm font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
          >
            Clear all filters
          </button>
        )}
      </div>
    );
  }

  const headerCell = (key: string, label: string, column?: ScreenerColumn, sticky = false) => {
    const active = sort === key;
    // Text columns (e.g. score status) can't be ranked; the API sorts numbers and names only.
    const sortable = column?.unit !== 'text';
    return (
      <th
        key={key}
        scope="col"
        aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`group/th border-b border-zinc-200 bg-zinc-50 px-3 py-2.5 text-xs font-semibold text-zinc-500 dark:border-zinc-800 dark:bg-zinc-800 dark:text-zinc-400 ${
          sticky ? 'sticky left-0 z-20 border-r border-r-zinc-100 text-left dark:border-r-zinc-800' : 'text-right'
        }`}
      >
        <span className={`inline-flex items-center gap-1 ${sticky ? '' : 'justify-end'}`}>
          {sortable ? (
            <button
              type="button"
              onClick={() => onSort(key)}
              className={`inline-flex items-center gap-1 whitespace-nowrap hover:text-zinc-900 dark:hover:text-white ${active ? 'text-zinc-900 dark:text-white' : ''}`}
            >
              {label}
              <SortIcon active={active} order={order} />
            </button>
          ) : (
            <span className="whitespace-nowrap">{label}</span>
          )}
          {column && <InfoTooltip title={column.label} text={column.description} />}
        </span>
      </th>
    );
  };

  return (
    <div
      className={`overflow-x-auto rounded-2xl border border-zinc-200 bg-white transition-opacity dark:border-zinc-800 dark:bg-zinc-900 ${
        loading && !showSkeleton ? 'opacity-60' : ''
      }`}
      aria-busy={loading}
    >
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {headerCell('name', 'Company', undefined, true)}
            {columns.map((column) => headerCell(column.key, column.label, column))}
          </tr>
        </thead>
        <tbody>
          {showSkeleton
            ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index} className="group">
                  <td className={`${STICKY_CELL} border-b border-b-zinc-100 px-3 py-3 dark:border-b-zinc-800`}>
                    <div className="h-4 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                    <div className="mt-1.5 h-3 w-20 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/60" />
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className="border-b border-zinc-100 px-3 py-3 dark:border-zinc-800">
                      <div className="ml-auto h-4 w-16 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const href = stockHref(row.exchange, row.symbol);
                return (
                  <tr
                    key={`${row.exchange}:${row.symbol}`}
                    onClick={() => router.push(href)}
                    className="group cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    <td className={`${STICKY_CELL} border-b border-b-zinc-100 px-3 py-2.5 dark:border-b-zinc-800`}>
                      <Link
                        href={href}
                        onClick={(event) => event.stopPropagation()}
                        className="block max-w-[9rem] sm:max-w-[16rem]"
                      >
                        <span className="block truncate font-medium text-zinc-900 group-hover:text-emerald-700 dark:text-zinc-100 dark:group-hover:text-emerald-400">
                          {row.name}
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                          <span className="font-mono">{row.symbol}</span>
                          <ExchangeBadge exchange={row.exchange} />
                        </span>
                      </Link>
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="whitespace-nowrap border-b border-zinc-100 px-3 py-2.5 text-right dark:border-zinc-800"
                      >
                        <MetricValue value={row[column.key] ?? null} column={column} />
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}
